<template>
  <v-row>
    <v-col cols="12" lg="12">
      <CardSimple v-if="isRequired('planning-criteria')" title="Planning Criteria">
        <InputYearly
          v-model="computedInput.capacity_margin"
          :error-messages="$utils.getIfExists(errors, ['input', 'capacity_margin'])"
          :label="schema.capacity_margin.title"
          :hint="schema.capacity_margin.tooltip"
          :suffix="schema.capacity_margin.unit"
          type="number"
          :num_years="num_years"
          fill
          :fill-value="schema.capacity_margin.items.default"
          @change="$emit('change')"
        ></InputYearly>
        <v-text-field
          v-model.number="computedInput.frequency_response"
          :error-messages="$utils.getIfExists(errors, ['input', 'frequency_response', 'messages'])"
          :label="schema.frequency_response.title"
          :hint="schema.frequency_response.tooltip"
          :suffix="schema.frequency_response.unit"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.storage_frequency_percentage"
          :error-messages="
            $utils.getIfExists(errors, ['input', 'storage_frequency_percentage', 'messages'])
          "
          :label="schema.storage_frequency_percentage.title"
          :hint="schema.storage_frequency_percentage.tooltip"
          :suffix="schema.storage_frequency_percentage.unit"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
        <InputYearly
          v-model="computedInput.storage_capacity_limit"
          :error-messages="$utils.getIfExists(errors, ['input', 'storage_capacity_limit'])"
          :num_years="num_years"
          :label="schema.storage_capacity_limit.title"
          :hint="schema.storage_capacity_limit.tooltip"
          :suffix="schema.storage_capacity_limit.unit"
          type="number"
          @change="$emit('change')"
          fill
          :fill-value="schema.storage_capacity_limit.items.default"
        ></InputYearly>
      </CardSimple>
    </v-col>
    <v-col cols="12" lg="12">
      <CardSimple v-if="isRequired('scarcity-prices')" title="Scarcity Prices">
        <v-text-field
          v-model.number="computedInput.dump_energy_cost"
          :error-messages="$utils.getIfExists(errors, ['input', 'dump_energy_cost', 'messages'])"
          :label="schema.storage_frequency_percentage.title"
          :hint="schema.storage_frequency_percentage.tooltip"
          :suffix="schema.storage_frequency_percentage.unit"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.lost_load_value"
          :error-messages="$utils.getIfExists(errors, ['input', 'lost_load_value', 'messages'])"
          :label="schema.lost_load_value.title"
          :hint="schema.lost_load_value.tooltip"
          :suffix="schema.lost_load_value.unit"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-for="(reserve, index) in input.reserves"
          :key="`reserve_${reserve.name}${index}`"
          v-model.number="reserve.loss_value"
          :error-messages="
            $utils.getIfExists(errors, ['input', 'reserves', index, 'loss_value', 'messages'])
          "
          :label="`Value of Lost ${reserve.name}`"
          suffix="$/MW"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.min_expost_price"
          :error-messages="$utils.getIfExists(errors, ['input', 'min_expost_price', 'messages'])"
          label="Minimum Ex-Post Price"
          suffix="$/MWh"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.max_expost_price"
          :error-messages="$utils.getIfExists(errors, ['input', 'max_expost_price', 'messages'])"
          label="Maximum Ex-Post Price"
          suffix="$/MWh"
          type="number"
          @change="$emit('change')"
        ></v-text-field>
      </CardSimple>
    </v-col>
    <v-col cols="12" lg="12">
      <SystemReserve
        v-if="isRequired('reserves')"
        title="Reserves"
        :error-messages="$utils.getIfExists(errors, ['input', 'reserves'])"
        :reserves.sync="input.reserves"
        :num_years="num_years"
        popout
        @change="$emit('change')"
      ></SystemReserve>
    </v-col>
    <v-col cols="12" lg="12">
      <CardSimple v-if="isRequired('elcc-peaker-generator')" title="ELCC Peaker Generator">
        <v-text-field
          v-model="computedInput.elcc_peaker_generator.source"
          :label="peakerSchema.source.title"
          :hint="peakerSchema.source.tooltip"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.elcc_peaker_generator.co2_rate"
          :label="peakerSchema.co2_rate.title"
          :hint="peakerSchema.co2_rate.tooltip"
          :suffix="peakerSchema.co2_rate.unit"
          type="number"
          :min="peakerSchema.co2_rate.minimum"
          :max="peakerSchema.co2_rate.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-if="input.elcc_peaker_generator.methodology === 'Net-Load relative to New Peaker'"
          v-model.number="computedInput.elcc_peaker_generator.derate_summer"
          :label="peakerSchema.derate_summer.title"
          :hint="peakerSchema.derate_summer.tooltip"
          :suffix="peakerSchema.derate_summer.unit"
          type="number"
          :min="peakerSchema.derate_summer.minimum"
          :max="peakerSchema.derate_summer.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-if="input.elcc_peaker_generator.methodology === 'Net-Load relative to New Peaker'"
          v-model.number="computedInput.elcc_peaker_generator.derate_winter"
          :label="peakerSchema.derate_winter.title"
          :hint="peakerSchema.derate_winter.tooltip"
          :suffix="peakerSchema.derate_winter.unit"
          type="number"
          :min="peakerSchema.derate_winter.minimum"
          :max="peakerSchema.derate_winter.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.elcc_peaker_generator.fom"
          :label="peakerSchema.fom.title"
          :hint="peakerSchema.fom.tooltip"
          :suffix="peakerSchema.fom.unit"
          type="number"
          :min="peakerSchema.fom.minimum"
          :max="peakerSchema.fom.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-row>
          <v-col cols="6"
            ><v-select
              v-model="computedInput.elcc_peaker_generator.fuel_unit"
              :label="peakerSchema.fuel_unit.title"
              :items="peakerSchema.fuel_unit.enum"
              :hint="peakerSchema.fuel_unit.tooltip"
              persistent-hint
              @change="$emit('change')"
            ></v-select
          ></v-col>
          <v-col cols="6"
            ><v-switch
              v-model="computedInput.elcc_peaker_generator.profile_fuel_price"
              :label="peakerSchema.profile_fuel_price.title"
              :hint="peakerSchema.profile_fuel_price.tooltip"
              persistent-hint
              @change="$emit('change')"
            ></v-switch
          ></v-col>
        </v-row>
        <InputYearly
          v-if="!input.elcc_peaker_generator.profile_fuel_price"
          v-model="computedInput.elcc_peaker_generator.fuel_price"
          :label="peakerSchema.fuel_price.title"
          :hint="peakerSchema.fuel_price.tooltip"
          :suffix="input.elcc_peaker_generator.fuel_unit"
          :num_years="num_years"
          fill
          :fill-value="peakerSchema.fuel_price.items.default"
          @change="$emit('change')"
        ></InputYearly>
        <InputYearlyProfile
          v-else
          v-model="computedInput.elcc_peaker_generator.fuel_price_profiles"
          :label="peakerSchema.fuel_price_profiles.title"
          :hint="peakerSchema.fuel_price_profiles.tooltip"
          :num_years="num_years"
          :suffix="input.elcc_peaker_generator.fuel_unit"
          :profile-type="peakerSchema.fuel_price_profiles.profileType"
          @change="$emit('change')"
        ></InputYearlyProfile>
        <v-text-field
          v-model.number="computedInput.elcc_peaker_generator.heat_rate"
          :label="peakerSchema.heat_rate.title"
          :hint="peakerSchema.heat_rate.tooltip"
          :suffix="peakerSchema.heat_rate.unit"
          type="number"
          :min="peakerSchema.heat_rate.minimum"
          :max="peakerSchema.heat_rate.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.elcc_peaker_generator.ramp_rate"
          :label="peakerSchema.ramp_rate.title"
          :hint="peakerSchema.ramp_rate.tooltip"
          :suffix="peakerSchema.ramp_rate.unit"
          type="number"
          :min="peakerSchema.ramp_rate.minimum"
          :max="peakerSchema.ramp_rate.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-text-field
          v-model.number="computedInput.elcc_peaker_generator.vom"
          :label="peakerSchema.vom.title"
          :hint="peakerSchema.vom.tooltip"
          :suffix="peakerSchema.vom.unit"
          type="number"
          :min="peakerSchema.vom.minimum"
          :max="peakerSchema.vom.maximum"
          @change="$emit('change')"
        ></v-text-field>
        <v-card flat>
          <v-card-title>
            <h6 class="text-h6">Peaker Reserves</h6>
          </v-card-title>
          <v-card-text>
            <InputReserve
              v-model="computedInput.elcc_peaker_generator.reserves"
              :label="peakerSchema.reserves.title"
              :hint="peakerSchema.reserves.tooltip"
              :reserves="input.reserves"
              :suffix="peakerSchema.reserves.unit"
              type="number"
              @change="$emit('change')"
            ></InputReserve>
          </v-card-text>
        </v-card>
      </CardSimple>
    </v-col>
  </v-row>
</template>

<script>
import { schemas } from "data-dictionary";
import inputSelectionMixin from "../mixins/inputSelectionMixin";

export default {
  name: "SystemReliability",
  components: {
    SystemReserve: () => import("@/components/SystemReserve.vue"),
    CardSimple: () => import("@/components/CardSimple.vue"),
    InputReserve: () => import("@/components/InputReserve.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
  },
  mixins: [inputSelectionMixin],
  props: ["num_years", "input", "errors"],
  data() {
    return {
      schema: schemas.filter((v) => v.$id === "input.schema.json")[0].properties,
      peakerSchema: {
        ...schemas.filter((v) => v.$id === "generator_elcc.schema.json")[0].properties,
        ...schemas
          .filter((v) => v.$id === "generator_elcc.schema.json")[0]
          .allOf.map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      },
    };
  },
  computed: {
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    computedCaseSettings() {
      return this.$attrs.settings;
    },
  },
  methods: {},
};
</script>

<style></style>
